import { useState, memo } from 'react';
import { format, formatDistanceToNow } from 'date-fns';
import { useApp } from '../../context/AppContext';
import { useConsumable as consumeSupply } from '../../utils/api';
import { parseLocalDate, fmtDuration, isOverdue } from '../../utils/dates';

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const FREQ_LABELS = {
  weekly: 'Every week',
  monthly: 'Every month',
  quarterly: 'Every 3 months',
  biannual: 'Every 6 months',
  annual: 'Every year',
  custom: 'Custom',
};

const MONTH_ABBR = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const fmtScheduledMonths = (months) => {
  if (!months || months.length === 0) return null;
  return [...months].sort((a, b) => a - b).map((m) => MONTH_ABBR[m - 1]).join(' · ');
};

const TaskCard = ({ task, onEdit, onDetail }) => {
  const { markTaskComplete, removeTask, appliances } = useApp();
  const linkedAppliance = task.linkedAppliance
    ? appliances.find((a) => a._id === task.linkedAppliance)
    : null;
  const [confirming, setConfirming] = useState(false);
  const [completedDate, setCompletedDate] = useState(todayStr());
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [supplyPrompt, setSupplyPrompt] = useState([]);

  const due = parseLocalDate(task.nextDue);
  const overdue = isOverdue(due);

  const relativeLabel = overdue
    ? `Overdue by ${formatDistanceToNow(due)}`
    : `Due ${formatDistanceToNow(due, { addSuffix: true })}`;

  const duration = fmtDuration(task.estimatedMinutes);

  const handleConfirm = async () => {
    setSubmitting(true);
    setConfirming(false);
    try {
      const linked = await markTaskComplete(task._id, { completedAt: completedDate, notes: notes || undefined });
      if (linked.length > 0) {
        setSupplyPrompt(linked);
      } else {
        setCompleting(true);
      }
    } catch {
      setConfirming(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSupplyUse = async (id) => {
    await consumeSupply(id, 1);
    setSupplyPrompt((prev) => prev.filter((s) => s._id !== id));
    if (supplyPrompt.length <= 1) setCompleting(true);
  };

  const handleSupplySkip = (id) => {
    setSupplyPrompt((prev) => prev.filter((s) => s._id !== id));
    if (supplyPrompt.length <= 1) setCompleting(true);
  };

  const handleStartConfirm = () => {
    setCompletedDate(todayStr());
    setNotes('');
    setConfirming(true);
  };

  return (
    <div className={`card task-card${overdue ? ' overdue' : ''}${completing ? ' completing' : ''}`}>
      <div
        className="task-card-body"
        onClick={() => !confirming && supplyPrompt.length === 0 && onDetail?.(task)}
        style={{ cursor: onDetail ? 'pointer' : 'default' }}
      >
        <div className="card-header">
          <h3>{task.title}</h3>
          <span className={`badge badge-${task.category}`}>{task.category}</span>
        </div>
        {task.description && <p className="card-description">{task.description}</p>}
        <div className="card-meta">
          <span className={`due-date${overdue ? ' text-danger' : ''}`}>{relativeLabel}</span>
          <span className="frequency">
            {fmtScheduledMonths(task.scheduledMonths) ?? FREQ_LABELS[task.frequency] ?? `Every ${task.frequency}`}
          </span>
          {duration && <span className="task-duration">⏱ {duration}</span>}
          {linkedAppliance && <span className="task-appliance">⚙ {linkedAppliance.name}</span>}
        </div>
        <p style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: '0.1rem' }}>
          {format(due, 'MMM d, yyyy')}
          {task.lastCompleted && ` · Last done ${format(parseLocalDate(task.lastCompleted), 'MMM d, yyyy')}`}
        </p>
      </div>

      {supplyPrompt.length > 0 ? (
        <div className="task-complete-form" onClick={(e) => e.stopPropagation()}>
          <p style={{ margin: '0 0 0.5rem', fontSize: '0.85rem', fontWeight: 600 }}>Did you use these supplies?</p>
          {supplyPrompt.map((s) => (
            <div key={s._id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.4rem' }}>
              <span style={{ flex: 1, fontSize: '0.85rem' }}>{s.name} <span style={{ color: '#94a3b8' }}>({s.quantityOnHand} on hand)</span></span>
              <button className="btn btn-success btn-sm" onClick={() => handleSupplyUse(s._id)}>Used 1</button>
              <button className="btn btn-secondary btn-sm" onClick={() => handleSupplySkip(s._id)}>Skip</button>
            </div>
          ))}
        </div>
      ) : confirming ? (
        <div className="task-complete-form" onClick={(e) => e.stopPropagation()}>
          <label className="task-complete-label">
            Completed on
            <input
              type="date"
              className="task-complete-date"
              value={completedDate}
              max={todayStr()}
              onChange={(e) => setCompletedDate(e.target.value)}
              autoFocus
            />
          </label>
          <input
            className="task-complete-notes"
            placeholder="Notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
          <div className="task-complete-actions">
            <button className="btn btn-success btn-sm" onClick={handleConfirm} disabled={submitting}>
              {submitting ? '…' : 'Confirm'}
            </button>
            <button className="btn btn-secondary btn-sm" onClick={() => setConfirming(false)}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="card-actions" onClick={(e) => e.stopPropagation()}>
          <button className="btn btn-success" onClick={handleStartConfirm}>
            ✓ Done
          </button>
          <button className="btn btn-secondary" onClick={() => onEdit(task)}>
            Edit
          </button>
          <button className="btn btn-danger" onClick={() => removeTask(task._id)}>
            Delete
          </button>
        </div>
      )}
    </div>
  );
};

export default memo(TaskCard);
